// frontend/src/lib/presence.js
// 在席（presence）マネージャ — Web の挙動リファレンス（docs/specs/presence_idle_offline_20260709.md）。
//
// 在席は「アクティブな時だけ heartbeat を送り、背景/非アクティブ/アイドル到達で即 away」で表現する。
// - アクティブ = タブが可視 かつ 直近操作が IDLE_LIMIT_MS 未満。
// - 可視だが無操作が IDLE_LIMIT_MS を超えたら away（＝オフライン）。操作再開で即 heartbeat（復帰）。
// - タブ非表示/離脱は即 away。離脱は keepalive で送出。
// ログイン中のみ start する（AuthContext / ArtistAuthContext から）。

import { requestApiJson } from '../api/client';

const HEARTBEAT_INTERVAL_MS = 60_000; // アクティブ中の heartbeat 間隔
const IDLE_LIMIT_MS = 5 * 60_000; // 無操作でオフラインにするまで（承認値: 5分）
const INTERACTION_THROTTLE_MS = 1_000; // 操作イベントの取りこぼしない範囲での間引き

const INTERACTION_EVENTS = ['pointerdown', 'keydown', 'scroll', 'touchstart', 'mousemove', 'wheel'];

let started = false;
let heartbeatTimer = null;
let lastInteractionAt = 0;
let lastInteractionWriteAt = 0;
let lastHeartbeatAt = 0;
let awaySent = false;

const now = () => Date.now();

const isVisible = () => typeof document === 'undefined' || document.visibilityState !== 'hidden';

const isActive = () => isVisible() && now() - lastInteractionAt < IDLE_LIMIT_MS;

function sendHeartbeat() {
  lastHeartbeatAt = now();
  awaySent = false;
  // fire-and-forget。失敗は次周期で自然に再送されるため握りつぶす。
  requestApiJson('/presence/heartbeat', { method: 'POST' }).catch(() => {});
}

function sendAway({ keepalive = false, force = false } = {}) {
  if (awaySent && !force) return;
  awaySent = true;
  requestApiJson('/presence/away', { method: 'POST', keepalive }).catch(() => {});
}

function handleInteraction() {
  const t = now();
  lastInteractionAt = t;
  if (t - lastInteractionWriteAt < INTERACTION_THROTTLE_MS) return;
  lastInteractionWriteAt = t;
  // away 状態からの操作再開は即オンライン化する。
  if (awaySent && isVisible()) sendHeartbeat();
}

function handleVisibilityChange() {
  if (!isVisible()) {
    sendAway();
    return;
  }
  // 復帰したら操作扱いにして即 heartbeat。
  lastInteractionAt = now();
  sendHeartbeat();
}

function handlePageHide() {
  // 離脱は確実に届けたいので keepalive、かつ直前が away でも強制送出。
  sendAway({ keepalive: true, force: true });
}

function tick() {
  if (isActive()) {
    if (now() - lastHeartbeatAt >= HEARTBEAT_INTERVAL_MS - 500) sendHeartbeat();
  } else {
    // 可視だが無操作、または非表示 → away（awaySent ガードで多重送出しない）。
    sendAway();
  }
}

export function startPresenceHeartbeat() {
  if (started || typeof window === 'undefined') return;
  started = true;
  lastInteractionAt = now();
  awaySent = false;
  INTERACTION_EVENTS.forEach((evt) =>
    window.addEventListener(evt, handleInteraction, { passive: true })
  );
  document.addEventListener('visibilitychange', handleVisibilityChange);
  window.addEventListener('pagehide', handlePageHide);
  window.addEventListener('beforeunload', handlePageHide);
  heartbeatTimer = window.setInterval(tick, HEARTBEAT_INTERVAL_MS);
  if (isActive()) sendHeartbeat();
}

export function stopPresenceHeartbeat({ sendAwayOnStop = true } = {}) {
  if (!started) return;
  started = false;
  INTERACTION_EVENTS.forEach((evt) => window.removeEventListener(evt, handleInteraction));
  document.removeEventListener('visibilitychange', handleVisibilityChange);
  window.removeEventListener('pagehide', handlePageHide);
  window.removeEventListener('beforeunload', handlePageHide);
  if (heartbeatTimer) {
    window.clearInterval(heartbeatTimer);
    heartbeatTimer = null;
  }
  // ログアウト等の停止時は、可能ならオフライン化しておく（セッションが残っていれば反映される）。
  if (sendAwayOnStop) sendAway({ force: true });
}

// テスト用途に内部状態を参照するためのハンドル（本番コードからは使わない）。
export const __presenceInternals = {
  isActive,
  isVisible,
  reset() {
    started = false;
    heartbeatTimer = null;
    lastInteractionAt = 0;
    lastInteractionWriteAt = 0;
    lastHeartbeatAt = 0;
    awaySent = false;
  },
};
